<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Detalle de liquidación</h1>
    </div>

    <!-- Resumen + acciones -->
    <wz-card class="mb-4">
      <template #header>
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-2">
            <span class="wz-card-title">{{ result.employeeFullName || 'Liquidación' }}</span>
            <wz-badge :variant="statusVariant(result.status)" pill>{{ statusLabel(result.status) }}</wz-badge>
          </div>
          <div class="flex gap-2">
            <template v-if="isBorrador">
              <wz-button variant="primary" @click="confirmModal = true">
                <i class="ri-check-line mr-1"></i>Confirmar
              </wz-button>
              <wz-button variant="outline" @click="recalcModal = true">
                <i class="ri-refresh-line mr-1"></i>Recalcular
              </wz-button>
              <wz-button variant="danger" @click="discardModal = true">
                <i class="ri-delete-bin-line mr-1"></i>Descartar
              </wz-button>
            </template>
            <template v-else-if="isConfirmado">
              <wz-button variant="primary" @click="openPayModal">
                <i class="ri-bank-card-line mr-1"></i>Registrar pago
              </wz-button>
              <wz-button variant="danger" @click="annulModal = true">
                <i class="ri-close-circle-line mr-1"></i>Anular
              </wz-button>
            </template>
            <template v-else-if="isPagado">
              <wz-button variant="danger" @click="annulModal = true">
                <i class="ri-close-circle-line mr-1"></i>Anular
              </wz-button>
            </template>
            <wz-tooltip text="Disponible en Fase 5">
              <wz-button variant="outline" :disabled="true">
                <i class="ri-file-pdf-line mr-1"></i>Descargar constancia
              </wz-button>
            </wz-tooltip>
          </div>
        </div>
      </template>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Empresa</small>
          <strong>{{ result.companyName || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">DNI</small>
          <strong>{{ result.employeeDni || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Fecha de cese</small>
          <strong v-local-date="result.terminationDate"></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Motivo</small>
          <strong>{{ result.cessationReasonName || '-' }}</strong>
        </div>
        <div v-if="result.terminationSubtype" class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Subtipo</small>
          <strong>{{ result.terminationSubtype }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Fecha de ingreso</small>
          <strong v-local-date="result.hireDate"></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Sueldo</small>
          <strong><span v-decimal:pen="result.baseSalary"></span></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Asig. familiar</small>
          <strong><span v-decimal:pen="result.familyAllowance"></span></strong>
        </div>
      </div>

      <!-- Beneficiario (solo fallecimiento) -->
      <div v-if="result.beneficiaryName" class="flex flex-wrap -mx-2 mt-2 pt-2 border-t">
        <div class="w-full mb-1"><small class="text-muted font-bold">Beneficiario</small></div>
        <div class="w-full md:w-1/3 px-2 mb-2">
          <small class="text-muted block">Nombre</small>
          <strong>{{ result.beneficiaryName }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2 mb-2">
          <small class="text-muted block">DNI</small>
          <strong>{{ result.beneficiaryDni || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2 mb-2">
          <small class="text-muted block">Parentesco</small>
          <strong>{{ result.beneficiaryRelationship || '-' }}</strong>
        </div>
      </div>
    </wz-card>

    <!-- Detalles del cálculo -->
    <wz-card title="Detalles del cálculo" class="mb-4">
      <wz-table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th class="text-right">Bruto</th>
            <th class="text-right">AFP</th>
            <th class="text-right">ONP</th>
            <th class="text-right">EsSalud</th>
            <th class="text-right">IR</th>
            <th v-if="hasAdjustments" class="text-right">Ajuste</th>
            <th class="text-right">Neto</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in result.details" :key="d.id">
            <td>{{ conceptLabel(d.concept) }}</td>
            <td class="text-right"><span v-decimal:pen="d.grossAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.afpAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.onpAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.essaludAmount"></span></td>
            <td class="text-right"><span v-decimal:pen="d.irAmount"></span></td>
            <td v-if="hasAdjustments" class="text-right"><span v-decimal:pen="d.manualAdjustment"></span></td>
            <td class="text-right"><span v-decimal:pen="d.netAmount"></span></td>
          </tr>
          <tr v-if="!result.details || !result.details.length">
            <td :colspan="hasAdjustments ? 8 : 7" class="text-center text-muted">Sin conceptos calculados.</td>
          </tr>
        </tbody>
        <tfoot v-if="result.details && result.details.length">
          <tr class="font-bold">
            <td class="text-right">Totales</td>
            <td class="text-right"><span v-decimal:pen="result.totalGross"></span></td>
            <td colspan="4" class="text-right">Deducciones: <span v-decimal:pen="result.totalDeductions"></span></td>
            <td v-if="hasAdjustments" class="text-right"></td>
            <td class="text-right"><span v-decimal:pen="result.totalNet"></span></td>
          </tr>
        </tfoot>
      </wz-table>

      <div v-if="result.details && result.details.length" class="mt-3">
        <h6 class="font-bold mb-2">Notas de cálculo</h6>
        <details v-for="d in result.details" :key="`note-${d.id}`" class="mb-1">
          <summary>{{ conceptLabel(d.concept) }}</summary>
          <span class="text-muted">{{ d.calculationNotes || '-' }}</span>
        </details>
      </div>
    </wz-card>

    <!-- Estado del proceso -->
    <wz-card title="Estado del proceso">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 px-2 mb-2">
          <small class="text-muted block">Calculado</small>
          <strong><span v-local-date:hour="result.calculatedAt"></span> — {{ result.calculatedBy || '-' }}</strong>
        </div>
        <div v-if="result.confirmedAt" class="w-full md:w-1/2 px-2 mb-2">
          <small class="text-muted block">Confirmado</small>
          <strong><span v-local-date:hour="result.confirmedAt"></span> — {{ result.confirmedBy || '-' }}</strong>
        </div>
        <div v-if="result.paidAt" class="w-full md:w-1/2 px-2 mb-2">
          <small class="text-muted block">Pagado</small>
          <strong>
            <span v-local-date:hour="result.paidAt"></span> — {{ result.paidBy || '-' }}
            <span class="text-muted">(pago <span v-local-date="result.paymentDate"></span>, {{ result.paymentMethod || '-' }})</span>
          </strong>
        </div>
        <div v-if="result.annulledAt" class="w-full md:w-1/2 px-2 mb-2">
          <small class="text-muted block">Anulado</small>
          <strong><span v-local-date:hour="result.annulledAt"></span> — {{ result.annulledBy || '-' }}</strong>
          <div class="text-muted">Motivo: {{ result.annulmentReason || '-' }}</div>
        </div>
      </div>
    </wz-card>

    <!-- Modales -->
    <wz-modal v-model="confirmModal" title="Confirmar liquidación">
      <p>¿Confirmar la liquidación de <strong>{{ result.employeeFullName }}</strong>? Esta acción es
        irreversible y el documento tendrá valor legal.</p>
      <template #footer>
        <wz-button variant="outline" @click="confirmModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="doConfirm">Confirmar</wz-button>
      </template>
    </wz-modal>

    <wz-modal v-model="payModal" title="Registrar pago">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 px-2 mb-3">
          <wz-datepicker label="Fecha de pago" required v-model="payForm.paymentDate" :max="todayIso()" />
        </div>
        <div class="w-full md:w-1/2 px-2 mb-3">
          <wz-select label="Método de pago" required v-model="payForm.paymentMethod"
            :items="paymentMethods" defaultOption="Seleccione un método" />
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline" @click="payModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!payForm.paymentDate || !payForm.paymentMethod" @click="doPay">
          Registrar pago
        </wz-button>
      </template>
    </wz-modal>

    <wz-modal v-model="annulModal" title="Anular liquidación">
      <wz-textarea label="Motivo de anulación" required v-model="annulForm.reason" :rows="3"
        maxlength="500" placeholder="Indique el motivo de la anulación" :error="annulError" />
      <template #footer>
        <wz-button variant="outline" @click="annulModal = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="!annulForm.reason || !annulForm.reason.trim()" @click="doAnnul">
          Anular
        </wz-button>
      </template>
    </wz-modal>

    <wz-modal v-model="recalcModal" title="Recalcular liquidación">
      <p>Recalcular descartará los valores actuales y volverá a calcular la liquidación con los mismos
        datos del cese. ¿Continuar?</p>
      <template #footer>
        <wz-button variant="outline" @click="recalcModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="doRecalculate">Recalcular</wz-button>
      </template>
    </wz-modal>

    <wz-modal v-model="discardModal" title="Descartar borrador">
      <p>El borrador se eliminará permanentemente. ¿Continuar?</p>
      <template #footer>
        <wz-button variant="outline" @click="discardModal = false">Cancelar</wz-button>
        <wz-button variant="danger" @click="doDiscard">Descartar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { SeveranceService } from '@/core/services'

const STATUS_META = {
  BORRADOR:   { label: 'Borrador',   variant: 'warning-solid' },
  CONFIRMADO: { label: 'Confirmado', variant: 'info-solid' },
  PAGADO:     { label: 'Pagado',     variant: 'success-solid' },
  ANULADO:    { label: 'Anulado',    variant: 'danger-solid' },
}

const CONCEPT_LABELS = {
  CTS_TRUNCA: 'CTS trunca',
  GRATIFICACION_TRUNCA: 'Gratificación trunca',
  BONIFICACION_9: 'Bonificación extraordinaria 9%',
  VACACIONES_TRUNCAS: 'Vacaciones truncas',
  VACACIONES_NO_GOZADAS: 'Vacaciones no gozadas',
  TRIPLE_VACACIONAL: 'Triple vacacional',
  INDEMNIZACION_DESPIDO: 'Indemnización por despido',
  REMUNERACION_PENDIENTE: 'Remuneración pendiente',
}

const PAYMENT_METHODS = [
  { key: 'TRANSFERENCIA_BANCARIA', label: 'Transferencia bancaria' },
  { key: 'CHEQUE', label: 'Cheque' },
  { key: 'EFECTIVO', label: 'Efectivo' },
]

export default {
  data() {
    return {
      result: { details: [] },
      paymentMethods: PAYMENT_METHODS,
      confirmModal: false,
      payModal: false,
      annulModal: false,
      recalcModal: false,
      discardModal: false,
      payForm: { paymentDate: null, paymentMethod: null },
      annulForm: { reason: '' },
      annulError: '',
    }
  },
  computed: {
    isBorrador() {
      return this.result.status === 'BORRADOR'
    },
    isConfirmado() {
      return this.result.status === 'CONFIRMADO'
    },
    isPagado() {
      return this.result.status === 'PAGADO'
    },
    hasAdjustments() {
      return (this.result.details || []).some(d => Number(d.manualAdjustment || 0) !== 0)
    },
  },
  methods: {
    conceptLabel(concept) {
      return CONCEPT_LABELS[concept] || concept
    },
    statusLabel(status) {
      const s = STATUS_META[status]
      return s ? s.label : status
    },
    statusVariant(status) {
      const s = STATUS_META[status]
      return s ? s.variant : 'dark'
    },
    todayIso() {
      const d = new Date()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.getSettlementDetail(this.$route.params.id)
        this.result = resp.payload || { details: [] }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la liquidación')
        this.$router.push({ name: 'severance-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    openPayModal() {
      this.payForm = { paymentDate: this.todayIso(), paymentMethod: null }
      this.payModal = true
    },
    async doConfirm() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.confirm(this.result.id)
        this.result = resp.payload || this.result
        this.confirmModal = false
        Alert.success('Liquidación confirmada')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al confirmar la liquidación')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doPay() {
      if (!this.payForm.paymentDate || !this.payForm.paymentMethod) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.pay(this.result.id, {
          paymentDate: this.payForm.paymentDate,
          paymentMethod: this.payForm.paymentMethod,
        })
        this.result = resp.payload || this.result
        this.payModal = false
        Alert.success('Pago registrado')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al registrar el pago')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doAnnul() {
      this.annulError = ''
      if (!this.annulForm.reason || !this.annulForm.reason.trim()) {
        this.annulError = 'El motivo es obligatorio'
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.annul(this.result.id, this.annulForm.reason.trim())
        this.result = resp.payload || this.result
        this.annulModal = false
        this.annulForm.reason = ''
        Alert.success('Liquidación anulada')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al anular la liquidación')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doRecalculate() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await SeveranceService.recalculate(this.result.id)
        this.recalcModal = false
        // recalculate devuelve un settlement NUEVO (id distinto): actualizo vista y URL.
        this.result = resp.payload || this.result
        if (resp.payload && resp.payload.id) {
          this.$router.replace({ name: 'severance-detail', params: { id: String(resp.payload.id) } })
        }
        Alert.success('Liquidación recalculada')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al recalcular la liquidación')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doDiscard() {
      try {
        this.$store.dispatch('app/loading', true)
        await SeveranceService.discard(this.result.id)
        this.discardModal = false
        Alert.success('Borrador descartado')
        this.$router.push({ name: 'severance-list', query: { companyId: this.result.companyId } })
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al descartar el borrador')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await this.loadData()
  },
}
</script>
